import { useState } from "react";

import { useDebounce } from "@/hooks/useDebounce";
import { EDatasetOrigin, GetDatasetListPayload } from "@/types/dataset";

interface DatasetFilters {
  selectedCountry: GetDatasetListPayload["country"] | "all";
  spatialAggregation: GetDatasetListPayload["spatial_aggregation_type"];
  published: GetDatasetListPayload["published"] | "all";
  /** Which product the dataset came from. */
  origin: EDatasetOrigin | "all";
}

interface UseDatasetFiltersProps {
  debounceTime?: number;
  initialFilters?: DatasetFilters;
  initialSearchQuery?: string;
}

interface DatasetFiltersReturn {
  filters: DatasetFilters;
  setFilters: React.Dispatch<React.SetStateAction<DatasetFilters>>;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  debouncedSearchQuery: string;
  isDefaultFilters: boolean;
  resetFilters: () => void;
}

const DEFAULT_FILTERS: DatasetFilters = {
  selectedCountry: "all",
  spatialAggregation: "all",
  published: "all",
  origin: "all"
};

/**
 * Custom hook to manage dataset filters state and logic
 */
export function useDatasetFilters({
  debounceTime = 1000,
  initialFilters,
  initialSearchQuery
}: UseDatasetFiltersProps = {}): DatasetFiltersReturn {
  const [filters, setFilters] = useState<DatasetFilters>(
    initialFilters ?? DEFAULT_FILTERS
  );
  const [searchQuery, setSearchQuery] = useState<string>(
    initialSearchQuery ?? ""
  );
  const debouncedSearchQuery = useDebounce(searchQuery, debounceTime);

  // Check if filters are at their default state
  const isDefaultFilters =
    filters.selectedCountry === DEFAULT_FILTERS.selectedCountry &&
    filters.spatialAggregation === DEFAULT_FILTERS.spatialAggregation &&
    filters.published === DEFAULT_FILTERS.published &&
    filters.origin === DEFAULT_FILTERS.origin &&
    !searchQuery;

  // Reset filters to default
  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchQuery("");
  };

  return {
    filters,
    setFilters,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    isDefaultFilters,
    resetFilters
  };
}
